import { Item } from "@thenounproject/lingo-core";
import _without from "lodash/without";
import _cloneDeep from "lodash/cloneDeep";

import { Args } from "@redux/actions/items/useReorderSectionItems";
import { getVersionedItemId } from "./items";

export function mutateLayoutByReorder(
  { reorderItemIds, displayOrder }: Args,
  sectionItems: Item[]
) {
  const sectionItemIds = sectionItems.map(getVersionedItemId);
  const proxiedDisplayOrder = displayOrder;
  /**
   * The reorderItemIds can be in any order as selected by the user.
   * If there is more than one item being reordered, make sure the
   * order is maintained by sorting them by their index in current state.
   */
  const orderedReorderItemIds = _cloneDeep(reorderItemIds);
  if (orderedReorderItemIds.length > 1) {
    const indexMap = sectionItemIds.reduce((acc, id, idx) => {
      acc[id] = idx;
      return acc;
    }, {});
    orderedReorderItemIds.sort((a, b) => indexMap[a] - indexMap[b]);
  }

  const reorderedSectionItemIds = _without(sectionItemIds, ...orderedReorderItemIds);

  const [direction, targetId] = displayOrder.split(":");
  const versionedTargetId = `${targetId}-0`;

  let index = reorderedSectionItemIds.indexOf(versionedTargetId);
  if (direction === "after") index += 1;

  /**
   * If the drop target happens to be in the items being reordered,
   * we need to calculate the insert index based on its initial position
   * & subtract by however many items before it are also being reordered.
   */
  if (orderedReorderItemIds.includes(versionedTargetId)) {
    index = sectionItemIds.indexOf(versionedTargetId);
    orderedReorderItemIds.forEach(itemId => {
      if (itemId === versionedTargetId) return;
      if (sectionItemIds.indexOf(itemId) < sectionItemIds.indexOf(versionedTargetId)) {
        index -= 1;
      }
    });
  }
  reorderedSectionItemIds.splice(index, 0, ...orderedReorderItemIds);

  return { reorderedSectionItemIds, displayOrder: proxiedDisplayOrder };
}

/**
 * In most cases, a reorder in which a user drops the 1st item in selection on
 * itself is invalid, so we just stop the drag event.  This function will return true
 * for the special cases where dropping a reorder item on itself is valid.
 * @param {String} dragPosition top | bottom | left | right
 * @param {String} guidePosition right | left | full
 */
export function isValidSelfDropPosition(
  dragPosition: "top" | "bottom" | "left" | "right",
  guidePosition: "right" | "left" | "full"
) {
  return (
    (dragPosition === "top" && guidePosition === "right") ||
    (dragPosition === "bottom" && guidePosition === "left") ||
    (dragPosition === "bottom" && guidePosition === "right") ||
    (dragPosition === "top" && guidePosition === "left")
  );
}
